import {ChangeDetectionStrategy, Component, computed, DOCUMENT, effect, inject, NgZone, OnDestroy, OnInit, Signal, WritableSignal} from '@angular/core';
import {AppService} from './services/app/app.service';
import {AuthService} from './services/auth/auth.service';
import {ElectronAppService} from './services/electron/app/electron-app.service';
import {DialogService} from './services/dialog/dialog.service';
import {SnackbarService} from './services/snackbar/snackbar.service';
import {AuthQuery} from './queries/auth.query';
import {CapacitorPluginsEnum} from './enums/capacitor-plugins.enum';
import {UrlParamsObserverService} from './services/url-params-observer/url-params-observer.service';
import {SearchService} from './services/search/search.service';
import {Capacitor, PluginListenerHandle, registerPlugin} from '@capacitor/core';
import {App} from '@capacitor/app';
import {ImportService} from './services/import/import.service';
import {Observable, Subscription} from 'rxjs';
import {AppQuery} from './queries/app.query';
import {AsyncPipe} from '@angular/common';
import {environment} from '../environments/environment';
import {NavigationService} from './services/navigation/navigation.service';
import {ShareToAppPlugin} from './models/share-to-app/share-to-app-plugin.model';
import {ShareToAppTextResult} from './models/share-to-app/share-to-app-text-result.model';
import {ShareToAppFilesResult} from './models/share-to-app/share-to-app-files.model';
import {RouterOutlet} from '@angular/router';
import {DocumentService} from './services/document/document.service';
import {debounceTime, distinctUntilChanged, filter} from 'rxjs/operators';
import {TutorialOptions} from './models/tutorial/tutorial-options';
import tutorialConfigs from '../configs/tutorial.config';
import {TutorialQuery} from './queries/tutorial.query';
import {LanguageService} from './services/language/language.service';
import {ListService} from './services/list/list.service';
import {BrowserService} from './services/browser/browser.service';
import {BrowserQuery} from './queries/browser.query';
import {VaultService} from './services/vault/vault.service';
import {CacheService} from './services/cache/cache.service';
import {CacheNameEnum} from './enums/cache/cache-name.enum';
import {SEARCH_CACHE_MAX_AGE_IN_SECONDS} from './constants/cache/cache-max-age-in-seconds.constants';
import {ServerSelectionService} from './services/server-selection/server-selection.service';
import {SentryService} from './services/sentry/sentry.service';
import {TutorialComponent} from './components/tutorial/tutorial.component';
import {DebugMenuComponent} from './components/debug-menu/debug-menu.component';
import {SystemInfoService} from './services/system-info/system-info.service';
import {SplashScreenComponent} from './components/splash-screen/splash-screen/splash-screen.component';
import {TelemetryService} from './services/telemetry/telemetry.service';
import {AZURE_SERVICE, AzureService} from './services/azure/azure.service';
import {LOGIN_TYPE} from './constants/login/login.type.constants';
import {PreferencesService} from './services/preferences/preferences.service';
import {UserService} from './services/user/user.service';
import {User} from './api/models/user';
import {AzureSignInMethodEnum} from './enums/azure-sign-in-method.enum';
import {AzureWrongServerError} from './errors/azure-wrong-server-error';
import {ServerAddress} from './models/server-address.model';
import {OrganisationError} from './errors/login-errors';
import {IPC_OPEN_DEEP_LINK, PROCESS_FILE_ARGUMENT} from './constants/shared.constants';
import {fetchFileAsBase64Content} from './util/file-utils.util';
import {OrganisationService} from './services/organisation/organisation.service';
import {HistoryOverlayComponent} from './components/history-overlay/history-overlay.component';
import {toSignal} from '@angular/core/rxjs-interop';
import {SearchOverviewComponent} from './components/search-overview/search-overview.component';
import {ThemeService} from './services/theme/theme.service';
import {EdgeToEdge} from '@capawesome/capacitor-android-edge-to-edge-support';
import {StatusBar} from '@capacitor/status-bar';
import {STATIC_CONFIGS} from '../configs/static.config';
import {UrlParamsService} from './services/url-params/url-params.service';
import {AiChatComponent} from './components/ai-chat/ai-chat.component';
import {AiChatService} from './services/ai/ai-chat.service';
import {ModalsComponent} from './components/modals/modals.component';
import {DeviceOrientationEnum} from './enums/device-orientation.enum';
import {AppStorageService} from './services/app-storage/app-storage.service';
import {AppStorageIdEnum} from './enums/app-storage-id.enum';
import {AppStorage} from './models/app-storage.model';
import {AmagnoStorageKeyEnum} from './enums/amagno-storage-key.enum';
import {AppDialogModal} from './models/modal/dialog/app-dialog-modal.model';
import {ModalService} from './services/modal/modal.service';

@Component({
    selector: 'app-root',
    host: { '[class.is-showing-ai-chat]': 'this.isShowingAiChat()' },
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        RouterOutlet,
        TutorialComponent,
        DebugMenuComponent,
        SplashScreenComponent,
        AsyncPipe,
        HistoryOverlayComponent,
        SearchOverviewComponent,
        AiChatComponent,
        SearchOverviewComponent,
        ModalsComponent
    ]
})
export class AppComponent implements OnInit, OnDestroy {
    protected isProduction: boolean;
    protected showDebugMenu$: Observable<boolean>;
    protected tutorialOptions: TutorialOptions;
    protected readonly isTutorialEnabled: Signal<boolean>;
    protected isInitialized$: Observable<boolean>;
    protected readonly showSplashScreen: WritableSignal<boolean>;
    protected readonly isShowingHistoryOverlay: Signal<boolean>;
    protected readonly isShowingSearchOverlay: Signal<boolean>;
    protected readonly hasSmallViewport: Signal<boolean>;
    protected readonly isShowingAiChat: Signal<boolean>;
    private readonly selectedServer: Signal<ServerAddress | undefined>;
    private readonly aiChatService = inject(AiChatService);
    private readonly appService = inject(AppService);
    private readonly appQuery = inject(AppQuery);
    private readonly authService = inject(AuthService);
    private readonly authQuery = inject(AuthQuery);
    private readonly tutorialQuery = inject(TutorialQuery);
    private readonly browserQuery = inject(BrowserQuery);
    private readonly browserService = inject(BrowserService);
    private readonly electronAppService = inject(ElectronAppService);
    private readonly dialogService = inject(DialogService);
    private readonly snackbarService = inject(SnackbarService);
    private readonly urlParamsObserverService = inject(UrlParamsObserverService);
    private readonly urlParamsService = inject(UrlParamsService);
    private readonly searchService = inject(SearchService);
    private readonly importService = inject(ImportService);
    private readonly navigationService = inject(NavigationService);
    private readonly ngZone = inject(NgZone);
    private readonly documentService = inject(DocumentService);
    private readonly languageService = inject(LanguageService);
    private readonly listService = inject(ListService);
    private readonly vaultService = inject(VaultService);
    private readonly cacheService = inject(CacheService);
    private readonly serverSelectionService = inject(ServerSelectionService);
    private readonly systemInfoService = inject(SystemInfoService);
    private readonly telemetryService = inject(TelemetryService);
    private readonly document = inject<Document>(DOCUMENT);
    private readonly azureService = inject<AzureService>(AZURE_SERVICE);
    private readonly preferencesService = inject(PreferencesService);
    private readonly userService = inject(UserService);
    private readonly subscription: Subscription;
    private readonly pluginListeners: Array<PluginListenerHandle | Promise<PluginListenerHandle>>;
    private readonly organisationService: OrganisationService = inject(OrganisationService);
    private readonly themeService: ThemeService = inject(ThemeService);
    private readonly sentryService = inject(SentryService);
    private readonly window = inject(DOCUMENT).defaultView ?? window;
    private readonly appStorageService = inject(AppStorageService);
    private readonly amagnoStorage: undefined | AppStorage;
    private readonly modalService = inject(ModalService);
    private orientationChangeWarningDialog: AppDialogModal | undefined;

    public constructor() {
        this.subscription = new Subscription();
        this.isProduction = environment.production;
        this.showDebugMenu$ = this.appQuery.isShowingDebugMenu$;
        this.tutorialOptions = tutorialConfigs;
        this.isTutorialEnabled = toSignal(this.tutorialQuery.isTutorialEnabled$, { initialValue: false });
        this.urlParamsObserverService.setup();
        this.urlParamsService.setup();
        this.appService.setup();
        this.isInitialized$ = this.appQuery.isInitialized$;
        this.isShowingHistoryOverlay = toSignal(this.appQuery.isShowingHistoryOverlay$, { initialValue: false });
        this.isShowingSearchOverlay = toSignal(this.appQuery.showSearchOverlay$, { initialValue: false });
        this.showSplashScreen = this.appService.getShowSplashScreenSignal();
        this.selectedServer = this.serverSelectionService.getSelectedServerSignal();
        this.isShowingAiChat = this.aiChatService.getIsShowingAiChatSignal();
        this.hasSmallViewport = toSignal(this.browserQuery.hasSmallViewport$, { initialValue: true });
        this.pluginListeners = [];
        this.amagnoStorage = this.appStorageService.getStorage(AppStorageIdEnum.AMAGNO);

        this.addAzureListener();
        this.addListenerForCapacitor();
        this.addConnectionChecker();

        computed(() => {
            const selectedServer = this.selectedServer();
            const bfaApiVersion = selectedServer?.serverInformation?.apiVersion;

            if (bfaApiVersion) {
                this.sentryService.setTag('app_bfa_api_version', bfaApiVersion);
            }
        });

        effect(async () => {
            if (this.systemInfoService.isAndroid() && this.systemInfoService.isCapacitor()) {
                const isDarkModeEnabled = this.themeService.getIsDarkModeSignal()();

                if (isDarkModeEnabled) {
                    await EdgeToEdge.setBackgroundColor({ color: STATIC_CONFIGS.ui.androidSystemBars.dark.backgroundColor });
                    await StatusBar.setStyle({ style: STATIC_CONFIGS.ui.androidSystemBars.dark.statusBarStyle });
                } else {
                    await EdgeToEdge.setBackgroundColor({ color: STATIC_CONFIGS.ui.androidSystemBars.light.backgroundColor });
                    await StatusBar.setStyle({ style: STATIC_CONFIGS.ui.androidSystemBars.light.statusBarStyle });
                }

            }
        });
    }

    public ngOnInit(): void {
        this.hideBootSpinner();
        this.addOrientationChangeSubscription();
        this.electronAppService.sendBootstrappedSignal();
        void this.initSubscriptions()
            .then();
    }

    public ngOnDestroy(): void {
        this.appService.setInitialized(false);
        this.subscription.unsubscribe();
        this.pluginListeners.forEach(pluginListener => {
            if (pluginListener instanceof Promise) {
                void pluginListener.then(pl => pl.remove());
            } else {
                void pluginListener.remove()
                    .then();
            }
        });
    }

    private handleOrientationChange(): void {
        if (this.window.visualViewport?.height && this.window.visualViewport.height <= STATIC_CONFIGS.ui.breakpoints.twoColumnMinHeight) {
            if (this.amagnoStorage?.has(AmagnoStorageKeyEnum.IGNORE_LANDSCAPE_WARNING)) {
                return;
            }

            this.orientationChangeWarningDialog = this.dialogService.showDialog({
                messageTranslationKey: 'APP_ORIENTATION_CHANGE_WARNING.MESSAGE',
                actionTranslationKey: 'APP_ORIENTATION_CHANGE_WARNING.BUTTON'
            });

            this.orientationChangeWarningDialog.afterClosed((closeByOrientationChange): void => {
                if (!closeByOrientationChange) {
                    this.amagnoStorage?.set(AmagnoStorageKeyEnum.IGNORE_LANDSCAPE_WARNING, true);
                }
            });
        }
    };

    private addOrientationChangeSubscription(): void {
        const orientationChangeEventEmitter = this.browserService.getOrientationChangeEventEmitter();
        const currentOrientation: DeviceOrientationEnum | undefined = this.browserQuery.getValue().orientation;

        if (currentOrientation === DeviceOrientationEnum.LANDSCAPE) {
            this.handleOrientationChange();
        }
        
        this.subscription.add(orientationChangeEventEmitter.subscribe((deviceOrientation: DeviceOrientationEnum | undefined) => {
            if (deviceOrientation === DeviceOrientationEnum.LANDSCAPE) {
                this.handleOrientationChange();
            } else if (this.orientationChangeWarningDialog) {
                this.modalService.closeModal(this.orientationChangeWarningDialog, true);
            }
        }));
    }

    private async initSubscriptions(): Promise<void> {
        this.addFullStorageListener();

        if (this.appQuery.getCurrentVersion() === '') {
            this.appService.setCurrentVersion(environment.version);
        }

        if (this.appQuery.getCurrentVersion() === environment.version) {
            await this.authService.setup();
            await this.updateNotification();
        } else {
            await this.logoutUserAfterVersionChange();
        }

        this.listService.cleanActiveListsFromStore();
        this.cacheService.setMaxAgeInSecondsByCacheName(CacheNameEnum.Search, SEARCH_CACHE_MAX_AGE_IN_SECONDS);
        this.appService.setInitialized(true);

        this.addSmallViewportSubscription();
        this.addIsLoggedInChangeSubscription();

        this.systemInfoService.init()
            .then(() => {
                this.browserService.setup();
                this.addLoginSubscription();
                this.addBodyClassForAndroidScrollbars();

                if (this.systemInfoService.isElectron()) {
                    this.setupElectronApp();
                }
            });
    }

    private setupElectronApp(): void {
        this.electronAppService.sendReadySignal();
        this.electronAppService.registerDeepLinkListener();
        this.electronAppService.registerFileImportListener();
        this.electronAppService.registerOpenFileListener();
        this.electronAppService.beforeAppQuit(async () => {
            if (!this.authQuery.isRememberUser()) {
                await this.authService.logout();
            }
        });
    }

    private addSmallViewportSubscription(): void {
        this.subscription.add(this.browserQuery.hasSmallViewport$.subscribe(hasSmallViewport => {
            const bodyElement = this.window.document.body;
            if (bodyElement) {
                bodyElement.classList.toggle('has-small-viewport', hasSmallViewport);
            } else {
                console.warn('Could not find body element, to set CSS class for small viewports');
            }
        }));
    }

    private addBodyClassForAndroidScrollbars(): void {
        const bodyElement = this.window.document.body;
        if (bodyElement) {
            bodyElement.classList.toggle('platform-android', this.systemInfoService.isAndroid());
        } else {
            console.warn('Could not find body element, to set platform CSS class');
        }
    }

    private addIsLoggedInChangeSubscription(): void {
        this.subscription.add(this.authQuery.isLoggedIn$.pipe(
                debounceTime(500),
                distinctUntilChanged()
            )
            .subscribe(isLoggedIn => {
                this.window.dispatchEvent(new CustomEvent<boolean>('loggedIn', {
                    detail: isLoggedIn
                }));
            }));
    }

    private addLoginSubscription(): void {
        this.subscription.add(this.authQuery.isLoggedIn$.pipe(
                filter(isLoggedIn => isLoggedIn)
            )
            .subscribe(async () => {
                const loggedInUserId = this.authQuery.getUserId();
                if (loggedInUserId) {
                    const user: User | undefined = await this.userService.getUser(loggedInUserId);
                    if (user) {
                        this.languageService.setCurrentIntlLocale(new Intl.Locale(user.languageCultureName));
                        this.authService.updateUserAuthData(user);
                    }
                }
                await this.vaultService.initVaults();
                this.initPlatformDirectories();
                this.navigationService.setAfterLoginNavigation();
                await this.preferencesService.fetchPreferences();

                const systemInfo = await this.systemInfoService.getSystemInfo();

                if (environment.sendTelemetryData) {
                    await this.telemetryService.send(systemInfo);
                }

                this.sentryService.setSystemInfoTags(systemInfo);
            }));
    }

    private async logoutUserAfterVersionChange(): Promise<void> {
        this.appService.setCurrentVersion(environment.version);
        await this.authService.logout(true);
        await this.navigationService.navigateToLogin();
        this.snackbarService.showInfo('LOGOUT_AFTER_UPDATE');
    }

    private async updateNotification(): Promise<void> {
        if (this.authQuery.getIsLoggedIn()) {
            const selectedServer = this.serverSelectionService.getSelectedServer();
            if (selectedServer) {
                await this.serverSelectionService.fetchServerInformationAndGetUpdatedServerAddress(selectedServer);
            }
        }
    }

    private addFullStorageListener(): void {
        this.window.addEventListener('fullStorageError', (() => {
            console.info('Tried to add data to full localstorage! Clear Stores..');
            localStorage.removeItem('__filling_localstorage__');
            this.authService.clearStores(false);
        }));
    }

    private hideBootSpinner(): void {
        // The DOM element shows a loading spinner while the app is opening (otherwise it would show "loading..").
        // Once the app is up, we hide it once. The animation is also smoother/not choppy and we prevent duplicated content.
        const elem = this.document.querySelector('#loading-spinner');
        if (elem) {
            elem.classList.remove('show');
        }
    }

    private initPlatformDirectories(): void {
        if (this.systemInfoService.isElectron()) {
            this.electronAppService.tryInitElectronApp();
        }
    }

    // TODO: device by platform or environment
    private addListenerForCapacitor(): void {
        if (Capacitor.isPluginAvailable(CapacitorPluginsEnum.shareToApp)) {
            const shareToApp = registerPlugin<ShareToAppPlugin>('ShareToApp');

            this.pluginListeners.push(shareToApp.addListener('shareToAppFiles', this.appService.handleExternalEvent<ShareToAppFilesResult>(eventData => {
                this.authService.continueAfterLoggedIn(() => {
                    if (eventData.error) {
                        if (eventData.error.includes('FILES_TO_LARGE_')) {
                            const maxSizeInMb = Number.parseInt(eventData.error.split('FILES_TO_LARGE_')[1], 10) / 1024 / 1024;
                            const maxSize = maxSizeInMb + 'mb';
                            this.snackbarService.showError('IMPORT.ERROR_FILES_TO_LARGE', undefined, { maxSize });
                        } else {
                            this.snackbarService.showError('IMPORT.ERROR_GENERIC', new Error(eventData.error));
                        }
                    } else {
                        let fileData: Array<{ name: string; file: string; filepath: string }> = [];
                        const streamData = eventData.files as string;
                        if (streamData) {
                            fileData = JSON.parse(streamData);
                        }
                        if (fileData.length > 0) {
                            fileData.forEach(file => file.file = Capacitor.convertFileSrc(file.file));
                            this.importService.addFromAndroid(fileData);
                        }
                    }
                    return Promise.resolve(true);
                });
            })));

            this.pluginListeners.push(shareToApp.addListener('shareToAppText', this.appService.handleExternalEvent<ShareToAppTextResult>(eventData => {
                this.authService.continueAfterLoggedIn(async () => {
                    let searchTerm = eventData.searchTerm;
                    if (searchTerm?.includes('\n')) {
                        const regexResult = /"([^"]+)"\n/gm.exec(searchTerm); // Get searchTerm without line-break and quotes
                        if (regexResult && regexResult.length > 0) {
                            searchTerm = regexResult[1];
                        }
                    }

                    if (searchTerm?.includes(environment.deepLinkPrefix)) {
                        await this.documentService.openDocumentViaDeepLink(searchTerm);
                    } else if (searchTerm) {
                        await this.searchService.openSearch(searchTerm);
                    }
                    return true;
                });
            })));
        }

        // only for android / iOS / Windows
        if (!this.systemInfoService.isWebApp() && !this.systemInfoService.isElectron() && Capacitor.isPluginAvailable(CapacitorPluginsEnum.app)) {
            if (Capacitor.addListener) {
                this.pluginListeners.push(Capacitor.addListener('App', IPC_OPEN_DEEP_LINK, this.appService.handleExternalEvent((eventData: unknown) => {
                    const url = (eventData as { url: string }).url + '';
                    this.authService.continueAfterLoggedIn(async () => {
                        if (url) {
                            if (url.startsWith('file://')) {
                                const fileName = url.substring(url.lastIndexOf('/') + 1)
                                    .split('%20')
                                    .join(' ');
                                const base64File = await fetchFileAsBase64Content(Capacitor.convertFileSrc(url));
                                this.importService.addFromIOS([
                                    {
                                        name: fileName,
                                        file: base64File
                                    }
                                ]);
                            } else if (url.startsWith(environment.deepLinkPrefix + 'importFiles?filePaths=')) {
                                let filePaths: string[] = url.replace(environment.deepLinkPrefix + 'importFiles?filePaths=', '')
                                    .split('&');
                                filePaths = filePaths.map(filePath => decodeURIComponent(filePath));
                                const files: Array<{ name: string; file: string }> = [];
                                await Promise.all(filePaths.map(async (filePath: string) => {
                                    const fileName = filePath.substring(filePath.lastIndexOf('/') + 1);
                                    const base64File = await fetchFileAsBase64Content(Capacitor.convertFileSrc(filePath));
                                    const file = {
                                        name: fileName,
                                        file: base64File
                                    };
                                    files.push(file);
                                }));
                                this.importService.addFromIOS(files);
                            } else if (url.startsWith(environment.deepLinkPrefix + 'search?text=')) {
                                const searchTermRegExp = new RegExp('^' + environment.deepLinkPrefix + 'search\\?text=(?<searchTerm>.*)$');
                                let [, searchTerm] = searchTermRegExp.exec(url) || [];
                                if (searchTerm.includes(environment.deepLinkPrefix)) {
                                    await this.documentService.openDocumentViaDeepLink(searchTerm);
                                } else {
                                    searchTerm = decodeURIComponent(searchTerm);
                                    await this.searchService.openSearch(searchTerm);
                                }
                            } else if (!url.includes(PROCESS_FILE_ARGUMENT)) {
                                await this.documentService.openDocumentViaDeepLink(url);
                            }
                        }
                        return true;
                    });
                })));
            }
            this.pluginListeners.push(App.addListener('backButton', () => {
                    this.ngZone.run(() => {
                        this.navigationService.goBackInHistory();
                    });
                })
                .then());
        }
    }

    private addConnectionChecker(): void {
        this.appQuery.isDisconnected$.subscribe(isOffline => {
            const isLoggedIn = this.authQuery.getIsLoggedIn();
            if (!isOffline) {
                this.dialogService.closeConnectionLostDialog();
            } else if (isLoggedIn) {
                this.connectionLost();
            }
        });
    }

    private connectionLost(): void {
        const selectedServer = this.selectedServer();
        if (!selectedServer) {
            return;
        }

        this.dialogService.showConnectionLostDialog(async () => {
                try {
                    const serverInformation = await this.serverSelectionService.getServerInformation(selectedServer.url);
                    this.serverSelectionService.updateServerAddressByServerInformation(selectedServer, serverInformation);

                    return !!serverInformation;
                } catch {
                    this.serverSelectionService.updateServerAddressByServerInformation(selectedServer);
                    return false;
                }
            })
            .then(() => {
                this.snackbarService.showSuccess('ERROR.CONNECTION_LOST.SUCCESS');
                this.appService.setIsDisconnected(false);
            })
            .catch(async () => {
                await this.authService.logout();
                this.window.location.href = '/auth/login';
            });
    }

    private addAzureListener(): void {
        this.azureService.setup()
            .then(() => {
                this.subscription.add(this.azureService.onSignIn.subscribe(async (userData) => {
                    if (!userData) {
                        return;
                    }

                    this.organisationService.setLastOrganisationLoginError(undefined);
                    try {
                        await this.authService.login(userData.username, userData.password, true, LOGIN_TYPE.AZURE);
                        const loggedInUserId = this.authQuery.getUserId();
                        if (loggedInUserId) {
                            const user: User | undefined = await this.userService.getUser(loggedInUserId);
                            if (user) {
                                this.languageService.setCurrentIntlLocale(new Intl.Locale(user.languageCultureName));
                                this.authService.updateUserAuthData(user);
                            }
                        }

                        await this.authService.showSystemMessageIfSet();

                        await this.navigationService.navigateAfterLogin();

                        this.appService.setShowSplashScreen(false, 0);
                    } catch (error) {
                        this.appService.setShowSplashScreen(false, 0);

                        if (error instanceof AzureWrongServerError) {
                            this.snackbarService.showError('ERROR_AZURE_WRONG_SERVER_ERROR', error as Error);
                        } else if (error instanceof OrganisationError) {
                            this.organisationService.setLastOrganisationLoginError(error);
                        } else {
                            this.snackbarService.showError('ERROR.LOGIN.DEFAULT', error as Error);
                        }

                        if (this.authQuery.getIsLoggedIn()) {
                            await this.authService.logout(true);
                            await this.navigationService.navigateToLogin();
                        }
                    }
                }));

                this.subscription.add(this.azureService.onSignInFailure.pipe(
                        filter((interActionType: AzureSignInMethodEnum) => interActionType !== AzureSignInMethodEnum.Silent)
                    )
                    .subscribe((interactionType) => {
                        this.appService.setShowSplashScreen(false, 0);
                        this.authService.logout(true)
                            .then();
                        if (interactionType !== AzureSignInMethodEnum.None) {
                            this.snackbarService.showError('ERROR_AZURE_LOGIN_FAILED');
                        }
                    }));
            });
    }
}
